import { formatClockTime, formatDate } from '@/lib/format';
import { prepareFileForUpload } from '@/lib/prepare-file-for-upload';
import type { AttendanceStatus } from '@/services/api/attendance/attendance';
import { API_BASE_URL, fetchApi } from '@/services/api-client';
import type { components } from '@/services/openapi';

export const EDP_PATH = '/edp';

/**
 * Longer than the 30s every other request gets. A slip is shrunk before it is sent, but a scanned
 * PDF has nothing to shrink, and the school's uplink is slow enough that the default cut uploads
 * off part-way — which reaches the office as a half-arrived multipart form rather than as a
 * timeout, because the API notices the truncated body first.
 */
const UPLOAD_TIMEOUT_MS = 120_000;

export type EdpRoundStatus = 'draft' | 'proposals_open' | 'in_review' | 'closed';
export type EdpProposalStatus =
  'submitted' | 'changes_requested' | 'approved' | 'waitlisted' | 'rejected' | 'withdrawn';
export type EdpCourseStatus = 'approved' | 'open_for_registration' | 'confirmed' | 'full' | 'waitlist' | 'cancelled';
export type EdpCategory = 'co_curricular' | 'extra_curricular';
export type EdpDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
/** `special` is not a slot: it is the block a special-date meeting's register is marked under,
 *  which is what keeps the attendance key NOT NULL for a meeting that belongs to no block. */
export type EdpBlock = 'block1' | 'block2' | 'block3' | 'special';

/** Every block, for validating one that arrived as text — a query string, a stored preference. */
export const EDP_BLOCKS: EdpBlock[] = ['block1', 'block2', 'block3', 'special'];
export type EdpDaysPerWeek = 'd1' | 'd2' | 'd3' | 'd4' | 'd5' | 'flexible';
export type EdpTriState = 'yes' | 'no' | 'maybe';
export type EdpSupportAnswer = 'yes' | 'no' | 'na' | 'possibly';
export type EdpPurchaseAnswer = 'no' | 'yes' | 'possibly';
export type EdpFacility =
  | 'regular_classroom'
  | 'early_years_classroom'
  | 'art_room'
  | 'music_room'
  | 'library'
  | 'makerspace'
  | 'computer_area'
  | 'multipurpose_hall'
  | 'gym_sports'
  | 'swimming_pool'
  | 'outdoor_area'
  | 'eco_lab'
  | 'other';

export const EDP_DAY_ORDER: EdpDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Display labels live next to the types so every EDP screen renders a band or a
 * block the same way. Times are baked into the block labels on purpose — staff
 * think in "the 15:40 one", not "Block 1".
 */
export const EDP_DAY_LABELS: Record<EdpDay, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

/**
 * The same days, short enough to sit in a table cell. A twice-weekly course written out in full
 * — "Monday · Block 1 — 15:40–16:30, Thursday · Block 2 — 16:40–17:30" — wraps into a paragraph
 * and stops reading as two days at all, which is the one thing the office needs to see.
 */
export const EDP_DAY_SHORT_LABELS: Record<EdpDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

const SATURDAY_SLOT_SHORT: Record<EdpBlock, string> = {
  block1: 'P2',
  block2: 'P3',
  block3: 'P4',
  special: '—',
};

const WEEKDAY_SLOT_SHORT: Record<EdpBlock, string> = {
  block1: 'B1',
  block2: 'B2',
  block3: 'B3',
  special: '—',
};

/**
 * A slot in two characters, for the same reason the day is in three. Saturday keeps its own
 * naming here as everywhere else: its slots are the school's morning periods, and abbreviating
 * one to "B1" would name an afternoon block the school does not ring on a Saturday.
 */
export function edpBlockShortLabel(day: EdpDay, block: EdpBlock): string {
  return day === 'sat' ? SATURDAY_SLOT_SHORT[block] : WEEKDAY_SLOT_SHORT[block];
}

/**
 * The slots each day has. A weekday runs two afternoon blocks; Saturday runs three morning
 * periods. Mirrors `EDP_BLOCKS_BY_DAY` in the API's `edp-round.entity.ts`.
 */
export const EDP_BLOCKS_BY_DAY: Record<EdpDay, EdpBlock[]> = {
  mon: ['block1', 'block2'],
  tue: ['block1', 'block2'],
  wed: ['block1', 'block2'],
  thu: ['block1', 'block2'],
  fri: ['block1', 'block2'],
  sat: ['block1', 'block2', 'block3'],
};

/**
 * Saturday's slots are the school's own morning bell — Periods 2, 3 and 4 of the ordinary day,
 * carrying the times `periods` rings them at, the way the afternoon blocks below carry theirs.
 */
const SATURDAY_SLOT_LABELS: Record<EdpBlock, string> = {
  block1: 'Period 2 — 08:50–09:40',
  block2: 'Period 3 — 09:55–10:45',
  block3: 'Period 4 — 10:45–11:35',
  special: 'Special date',
};

const WEEKDAY_SLOT_LABELS: Record<EdpBlock, string> = {
  block1: 'Block 1 — 15:40–16:30',
  block2: 'Block 2 — 16:40–17:30',
  block3: 'Block 3',
  special: 'Special date',
};

/**
 * Which EDP day a calendar date falls on, or null for a Sunday — the one day the school runs no
 * blocks at all. Parsed as a local calendar date (`T00:00:00`) rather than through `Date(value)`,
 * which reads a bare `YYYY-MM-DD` as UTC and lands on the day before west of Greenwich.
 */
export function edpDayOf(date: string): EdpDay | null {
  const weekday = new Date(`${date}T00:00:00`).getDay();
  return weekday === 0 ? null : (EDP_DAY_ORDER[weekday - 1] ?? null);
}

/** The blocks a course meets in on one date, in school order — empty when it does not meet. */
export function edpBlocksOn(sessions: { day: EdpDay; block: EdpBlock }[], date: string): EdpBlock[] {
  const day = edpDayOf(date);
  if (!day) return [];
  return EDP_BLOCKS_BY_DAY[day].filter(block => sessions.some(s => s.day === day && s.block === block));
}

/**
 * What to call a slot, which only means anything alongside its day: Saturday's slots are the
 * school's own morning periods, and calling one "Block 1 — 15:40" would name a time it is not.
 */
export function edpBlockLabel(day: EdpDay, block: EdpBlock): string {
  return day === 'sat' ? SATURDAY_SLOT_LABELS[block] : WEEKDAY_SLOT_LABELS[block];
}

/**
 * A slot named against several days at once — a proposal asking for a run of afternoons, or a row
 * of a printed week. Only the days that actually ring the slot get a say: Block 3 is Saturday's
 * Period 4 and no weekday rings a third slot at all, so a Monday-to-Saturday week names it
 * "Period 4" rather than contradicting the cell beneath it. A slot both halves ring is described
 * in weekday terms, which is the half a reader would otherwise misread as a morning.
 */
export function edpBlockLabelForDays(days: EdpDay[], block: EdpBlock): string {
  const rung = days.filter(day => EDP_BLOCKS_BY_DAY[day].includes(block));
  const saturdayOnly = rung.length > 0 && rung.every(day => day === 'sat');
  return edpBlockLabel(saturdayOnly ? 'sat' : 'mon', block);
}

/**
 * When a course meets, on one line. Every meeting is named against its own day rather than the
 * first one's, so a course that runs Friday afternoon and Saturday morning reads correctly on
 * both halves. One meeting renders exactly as the single day-and-block label always did.
 *
 * Tolerates a missing array: `sessions` reached the demand response only now, and an admin build
 * deployed ahead of the API must show a blank cell rather than crash the screen.
 */
export function edpSessionsLabel(sessions: { day: EdpDay; block: EdpBlock }[] | null | undefined): string {
  return (sessions ?? [])
    .map(session => `${EDP_DAY_LABELS[session.day]} · ${edpBlockLabel(session.day, session.block)}`)
    .join(', ');
}

/** A special-date meeting's own hours, the way every screen writes a span of time. */
export function edpMeetingTimes(meeting: { startsAt: string; endsAt: string }): string {
  return `${formatClockTime(meeting.startsAt.slice(0, 5))}–${formatClockTime(meeting.endsAt.slice(0, 5))}`;
}

/** One special-date meeting on one line: the date, then the hours it runs. */
export function edpMeetingLabel(meeting: { date: string; startsAt: string; endsAt: string }): string {
  return `${formatDate(meeting.date)} · ${edpMeetingTimes(meeting)}`;
}

/**
 * When a course meets, whichever way it meets: its afternoons, or the dates the school fixed
 * for it. One function so every screen answers the same way — a special-date course read
 * through `sessions` alone reports no schedule at all, which is the blank the office would then
 * have to guess at.
 */
export function edpScheduleLabel(
  course:
    | {
        scheduleKind?: EdpScheduleKind | null;
        sessions?: { day: EdpDay; block: EdpBlock }[] | null;
        meetings?: { date: string; startsAt: string; endsAt: string }[] | null;
      }
    | null
    | undefined
): string {
  if (course?.scheduleKind !== 'special_dates') return edpSessionsLabel(course?.sessions);
  return (course.meetings ?? []).map(edpMeetingLabel).join(', ');
}

/**
 * The afternoons a proposal asks for, on one line. Groups are numbered only when there is more
 * than one, because nearly every proposal is a single run and "Group 1:" in front of it is noise.
 *
 * Falls back to the day list and block list of a proposal submitted before groups existed. Those
 * two were independent, so their meaning was the cross-product — which is why they read as
 * "Monday, Wednesday · Block 1" rather than as a list of afternoons.
 */
export function edpRequestedSlotsLabel(proposal: {
  preferredGroups?: EdpPreferredGroup[] | null;
  preferredDays?: EdpDay[] | null;
  preferredBlocks?: EdpBlock[] | null;
}): string {
  const groups = proposal.preferredGroups;
  if (groups?.length) {
    if (groups.length === 1) return edpSessionsLabel(groups[0].sessions);
    return groups.map((group, index) => `${index + 1}: ${edpSessionsLabel(group.sessions)}`).join(' · ');
  }
  const days = proposal.preferredDays ?? [];
  const blocks = proposal.preferredBlocks ?? [];
  if (days.length === 0 && blocks.length === 0) return '';
  return `${days.map(day => EDP_DAY_LABELS[day]).join(', ')} · ${blocks
    .map(block => edpBlockLabelForDays(days, block))
    .join(' / ')}`;
}

export const EDP_CATEGORY_LABELS: Record<EdpCategory, string> = {
  co_curricular: 'Co-Curricular',
  extra_curricular: 'Extra-Curricular',
};

/** The same two words in the width of a pill, for a bar that has a course title to fit as well. */
export const EDP_CATEGORY_SHORT_LABELS: Record<EdpCategory, string> = {
  co_curricular: 'CO',
  extra_curricular: 'EXTRA',
};

export const EDP_STATUS_LABELS: Record<EdpProposalStatus, string> = {
  submitted: 'Submitted',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  waitlisted: 'Waitlisted',
  rejected: 'Not approved',
  withdrawn: 'Withdrawn',
};

export const EDP_FACILITY_LABELS: Record<EdpFacility, string> = {
  regular_classroom: 'Regular Classroom',
  early_years_classroom: 'Early Years Classroom',
  art_room: 'Art Room',
  music_room: 'Music Room',
  library: 'Library',
  makerspace: 'Makerspace / STEAM Area',
  computer_area: 'Computer / Technology Area',
  multipurpose_hall: 'Multipurpose Hall',
  gym_sports: 'Gym / Sports Area',
  swimming_pool: 'Swimming Pool',
  outdoor_area: 'Outdoor Area',
  eco_lab: 'Eco-Lab',
  other: 'Other',
};

export type EdpPerson = { id: string; displayName: string; email: string | null; photoUrl: string };

export type EdpRound = {
  id: string;
  schoolYearId: string;
  schoolYearName: string | null;
  name: string;
  status: EdpRoundStatus;
  proposalOpensAt: string;
  proposalClosesAt: string;
  registrationOpensAt: string | null;
  registrationClosesAt: string | null;
  firstLessonDate: string;
  lastLessonDate: string;
  /** What an ordinary course costs for the whole round, in whole baht — not a per-lesson rate. */
  basePrice: number;
  /** Whether families may be asked to pay for this round yet. Off until the office turns it on. */
  paymentsEnabled: boolean;
  /** `YYYY-MM-DD`, or null. Advisory — a late payment is still accepted. */
  paymentDueDate: string | null;
  /** When the opening announcement went out, or null if it has not. */
  paymentNoticeSentAt: string | null;
  acceptingProposals: boolean;
  proposalCount: number;
};

/** One afternoon a teacher is asking for. No room: which room is the office's decision. */
export type EdpPreferredSlot = { day: EdpDay; block: EdpBlock };

/**
 * One run of a proposed course: the afternoons it meets, and optionally the grades that run is
 * for. The same children attend every session of a group; a second group is the same activity
 * taught again to a different set of children, which is what a day list and a block list read as
 * a cross-product could never say.
 */
export type EdpPreferredGroup = { sessions: EdpPreferredSlot[]; gradeLevels?: number[] };

export type EdpCoursePayload = {
  title: string;
  category: EdpCategory;
  description: string;
  objectives: string;
  gradeLevels: number[];
  oneOnOne: boolean;
  /** Omitted only when correcting a proposal from before groups existed, which has none to
   *  state — the server then leaves the column as it is. */
  preferredGroups?: EdpPreferredGroup[];
  /** The teacher parents will see when a visitor runs the course. */
  externalInstructorName?: string;
  proposedMin: number;
  proposedMax?: number | null;
  additionalStudentsWithSupport: EdpSupportAnswer;
  preferredFacilities: EdpFacility[];
  preferredFacilityOther?: string;
  materialsEquipment: string;
  purchaseRequired: EdpPurchaseAnswer;
  purchaseDetails?: string;
  safetyRequired: boolean;
  safetyDetails?: string;
  courseOutcome?: string;
  additionalInfo?: string;
};

export type EdpProposalSummary = {
  id: string;
  reference: string;
  roundId: string;
  roundName: string;
  teacher: EdpPerson;
  positionDepartment: string;
  status: EdpProposalStatus;
  title: string;
  category: EdpCategory;
  gradeLevels: number[];
  oneOnOne: boolean;
  /** What the teacher asked for. Null on a proposal submitted before groups existed, which
   *  describes itself through the two deprecated lists below instead. */
  preferredGroups: EdpPreferredGroup[] | null;
  /** @deprecated Read-only history — see `preferredGroups`. */
  preferredDays: EdpDay[] | null;
  /** @deprecated Read-only history — see `preferredGroups`. */
  preferredBlocks: EdpBlock[] | null;
  proposedMin: number;
  proposedMax: number | null;
  courseId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type EdpModification = { field: string; from: string; to: string };

/** One meeting of a course: a day, a slot within it, and the room it uses. */
export type EdpCourseSession = { day: EdpDay; block: EdpBlock; roomId: string | null; roomName: string | null };

/**
 * Which of the two ways a course meets. `weekly` is nearly every course: a day and a block.
 * `special_dates` is a course the school fixed to enumerated dates instead — Sailing, whose six
 * afternoons are decided by the tide and run 16:00-17:30, spanning both blocks and borrowing
 * neither. A course keeps one kind or the other, never rows of both.
 */
export type EdpScheduleKind = 'weekly' | 'special_dates';

/** One enumerated meeting of a special-date course, carrying its own times because it has no block. */
export type EdpCourseMeeting = {
  date: string;
  /** `HH:MM`. */
  startsAt: string;
  endsAt: string;
  roomId: string | null;
  roomName: string | null;
};

export type EdpCourse = {
  id: string;
  reference: string;
  title: string;
  /**
   * Staff who help run it, and any of them already on another course in the same afternoon.
   * Present on the course detail only — the list endpoints do not carry them.
   */
  assistants?: { id: string; displayName: string }[];
  assistantWarnings?: string[];
  /** The office's own copy, genuinely editable after approval — see `configureCourse`. Never
   *  the proposal's own text, which is never overwritten. What a family reads on the family
   *  site is this field. */
  description: string;
  objectives: string | null;
  category: EdpCategory;
  gradeLevels: number[];
  oneOnOne: boolean;
  /** Every meeting of the course, in school order. Empty on a special-date course. */
  sessions: EdpCourseSession[];
  scheduleKind: EdpScheduleKind;
  /** The dates a special-date course meets on, earliest first. Empty on a weekly one. */
  meetings: EdpCourseMeeting[];
  /** The member of staff who teaches it, whose afternoon it books. Null when a teacher with no
   *  account does — `externalInstructorName` names them. Exactly one of the two is set. */
  instructor: EdpPerson | null;
  externalInstructorName: string | null;
  roomId: string | null;
  roomName: string | null;
  minStudents: number;
  maxStudents: number | null;
  /** Whole baht. Previously missing from this response — a price the office could set through
   *  `configureCourse` but never read back, which is what let a save silently blank an
   *  already-priced course. */
  price: number;
  /** Whether a family's free-place credit may be spent on this course. Off unless the office says so. */
  eligibleForFreePlaces: boolean;
  status: EdpCourseStatus;
  /** Closed to NEW requests while the round's window stays open. Everyone already queued keeps
   *  their request and their place in the queue. */
  registrationClosed: boolean;
};

export type EdpProposalEventKind =
  | 'submitted'
  | 'resubmitted'
  | 'edited_by_office'
  | 'changes_requested'
  | 'approved'
  | 'waitlisted'
  | 'rejected'
  | 'withdrawn'
  | 'comment'
  | 'concern_raised'
  | 'email_sent';

export type EdpProposalEvent = {
  id: string;
  kind: EdpProposalEventKind;
  actor: EdpPerson | null;
  body: string | null;
  fromStatus: EdpProposalStatus | null;
  toStatus: EdpProposalStatus | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

export type EdpProposalRevision = {
  id: string;
  version: number;
  snapshot: Record<string, unknown>;
  submittedBy: EdpPerson | null;
  createdAt: string;
};

/**
 * Spelled out explicitly rather than intersected with `EdpCoursePayload` — that type
 * is a request shape, where optional fields are correct because the client omits
 * them. This is a response shape: it must mirror what the server actually sends
 * (see `edp.response.ts`), where every one of these text fields comes back as
 * `T | null`, never omitted. Intersecting `T?` with `T | null` would silently
 * collapse the property to plain `T`, dropping both `null` and `undefined`.
 */
export type EdpProposalDetail = EdpProposalSummary & {
  description: string;
  objectives: string;
  /** The teacher parents will see when a visitor runs it. Null when the proposer teaches it. */
  externalInstructorName: string | null;
  /** @deprecated Read-only history — a group's own session count says this now. */
  daysPerWeek: EdpDaysPerWeek | null;
  /** @deprecated Read-only history — a second group says this now, and says which afternoons. */
  availableForTwoClasses: EdpTriState | null;
  additionalStudentsWithSupport: EdpSupportAnswer;
  preferredFacilities: EdpFacility[];
  preferredFacilityOther: string | null;
  materialsEquipment: string;
  purchaseRequired: EdpPurchaseAnswer;
  purchaseDetails: string | null;
  safetyRequired: boolean;
  safetyDetails: string | null;
  courseOutcome: string | null;
  additionalInfo: string | null;
  confirmationAcceptedAt: string;
  /** Null for the proposing staff member — the office's notes are private. */
  adminNotes: string | null;
  decidedAt: string | null;
  decidedBy: EdpPerson | null;
  course: EdpCourse | null;
  modifications: EdpModification[];
  /** Advisory only — the proposed maximum exceeds the band's standard. */
  capacityWarning: string | null;
  revisions: EdpProposalRevision[];
  events: EdpProposalEvent[];
};

export type EdpProposalFilters = {
  roundId?: string;
  status?: EdpProposalStatus;
  category?: EdpCategory;
  day?: EdpDay;
  block?: EdpBlock;
  search?: string;
};

export type SubmitEdpProposalsData = {
  roundId: string;
  positionDepartment: string;
  confirmationAccepted: boolean;
  courses: EdpCoursePayload[];
};

export type CreateEdpRoundData = {
  schoolYearId: string;
  name: string;
  proposalOpensAt: string;
  proposalClosesAt: string;
  registrationOpensAt?: string;
  registrationClosesAt?: string;
  firstLessonDate: string;
  lastLessonDate: string;
};

export type UpdateEdpRoundData = Partial<CreateEdpRoundData> & {
  status?: EdpRoundStatus;
  /** Whole baht, for the whole round. Refused, not clamped, when negative. */
  basePrice?: number;
  paymentsEnabled?: boolean;
  paymentDueDate?: string | null;
};

/** One date a special-date course meets on, as the office types it: `YYYY-MM-DD` and `HH:MM`. */
export type EdpCourseMeetingData = {
  date: string;
  startsAt: string;
  endsAt: string;
  roomId?: string | null;
};

/** One group of a course: its own meetings, grades, teacher and capacity, and its own roster. */
export type EdpCourseGroupData = {
  title?: string;
  sessions: { day: EdpDay; block: EdpBlock; roomId?: string | null }[];
  /** Narrower than the approval's, for the school's Junior/Upper split. Omitted means the
   *  group takes the approval's whole set, which is nearly every group. */
  gradeLevels?: number[];
  instructorId?: string;
  /** The teacher parents see when this group is not run by a member of staff. */
  externalInstructorName?: string;
  minStudents?: number;
  maxStudents?: number | null;
  /** Defaults to the answer given for the approval as a whole. */
  eligibleForFreePlaces?: boolean;
};

export type ApproveEdpProposalData = {
  title: string;
  category: EdpCategory;
  gradeLevels: number[];
  oneOnOne: boolean;
  /**
   * The groups to run this proposal as. One is the ordinary case. More than one is the same
   * activity run for different sets of children — which sessions cannot express, because
   * everyone in a course attends all of its meetings.
   */
  groups?: EdpCourseGroupData[];
  /** When the single group meets. Ignored when `groups` is given. Omitted on a special-date
   *  approval, which the API refuses alongside a weekly slot. */
  sessions?: { day: EdpDay; block: EdpBlock; roomId?: string | null }[];
  /**
   * Which kind of schedule the approval creates. A teacher's proposal always asks for a weekly
   * slot; the school decides a course meets on set dates when it approves it.
   */
  scheduleKind?: EdpScheduleKind;
  /** The dates a special-date approval meets on. Refused on a weekly one. */
  meetings?: EdpCourseMeetingData[];
  /** Omitted when no member of staff teaches it; then `externalInstructorName` names who does.
   *  One of the two is required — there is no fallback to whoever filed the proposal. */
  instructorId?: string;
  externalInstructorName?: string;
  minStudents: number;
  maxStudents?: number | null;
  /** Whether free-place credit may be spent on the course this approval creates. */
  eligibleForFreePlaces?: boolean;
  adminNotes?: string;
};

export type EdpDecisionData = { comment: string; adminNotes?: string };

/**
 * §11b: the office's course-facing configuration. `status` deliberately excludes
 * `confirmed`, `full` and `waitlist` — those are outcomes the placement logic derives
 * from enrolment counts, not values an administrator may set directly. See
 * `EDP_COURSE_STATUS_TRANSITIONS` for which moves the API will actually accept.
 */
export type ConfigureEdpCourseData = {
  /** Whole baht. Refused, not clamped, when negative. */
  price?: number;
  status?: EdpCourseStatus;
  /** Stop taking new requests for this one course while the round's window stays open for the
   *  rest. Not a status: everyone already queued keeps their request. */
  registrationClosed?: boolean;
  /** Whether free-place credit may be spent on this course. */
  eligibleForFreePlaces?: boolean;
  /** Genuinely editable after approval — the proposal it was copied from never changes. */
  title?: string;
  description?: string;
  objectives?: string;
  category?: EdpCategory;
  gradeLevels?: number[];
  oneOnOne?: boolean;
  /** Refused when that teacher already runs something in this round's day and block. Null hands
   *  the course to `externalInstructorName`; naming either one clears the other. */
  instructorId?: string | null;
  /** Replaces whoever assists it now. */
  assistantIds?: string[];
  /** The teacher parents see when no member of staff teaches it. */
  externalInstructorName?: string;
  /** When the course meets. Replaces the whole set, so dropping a day is leaving it out. */
  sessions?: { day: EdpDay; block: EdpBlock; roomId?: string | null }[];
  /** Switching this drops the rows of the kind the course is leaving; a course is never both. */
  scheduleKind?: EdpScheduleKind;
  /** The dates a special-date course meets on. Replaces the whole set, exactly like `sessions`. */
  meetings?: EdpCourseMeetingData[];
  minStudents?: number;
  /** Null clears the cap — "no fixed maximum" is a real answer, not a missing one. */
  maxStudents?: number | null;
};

export const EDP_COURSE_STATUS_LABELS: Record<EdpCourseStatus, string> = {
  approved: 'Approved (not yet open)',
  open_for_registration: 'Open for registration',
  confirmed: 'Confirmed',
  full: 'Full',
  waitlist: 'Waitlist only',
  cancelled: 'Cancelled',
};

/**
 * Mirrors `ALLOWED_STATUS_TRANSITIONS` in `edp-course-config.service.ts` on the API.
 * Kept here (rather than derived from an endpoint — none exists) so the UI never offers
 * a move the server will refuse; a control that always errors is worse than no control.
 * `confirmed`, `full`, `waitlist` and `cancelled` have no entries because the API allows
 * no administrator-typed move out of them. If the server's map changes, this must too.
 */
export const EDP_COURSE_STATUS_TRANSITIONS: Partial<Record<EdpCourseStatus, EdpCourseStatus[]>> = {
  approved: ['open_for_registration', 'cancelled'],
  open_for_registration: ['approved', 'cancelled'],
};

// ── Office registration & placement (subsystem 3) ───────────────────────────

export type EdpEnrolmentStatus = 'confirmed' | 'offered' | 'waitlisted' | 'declined' | 'withdrawn' | 'cancelled';
export type EdpEnrolmentSource = 'parent_request' | 'office';
export type EdpChoiceOutcome = 'pending' | 'placed' | 'full' | 'not_running' | 'superseded';
export type EdpDemandFlag = 'oversubscribed' | 'ready' | 'below_minimum' | 'placed';

/**
 * Office-facing wording. "Not running" rather than "cancelled" and "No place
 * this time" rather than "full" — the same instinct that made a rejected
 * proposal read as "Not approved".
 */
export const EDP_CHOICE_OUTCOME_LABELS: Record<EdpChoiceOutcome, string> = {
  pending: 'Not yet decided',
  placed: 'Has a place',
  full: 'No place this time',
  not_running: 'Not running',
  superseded: 'Covered by a higher choice',
};

export const EDP_ENROLMENT_STATUS_LABELS: Record<EdpEnrolmentStatus, string> = {
  confirmed: 'Confirmed',
  offered: 'Waiting for the family',
  waitlisted: 'Waitlisted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
};

export const EDP_DEMAND_FLAG_LABELS: Record<EdpDemandFlag, string> = {
  oversubscribed: 'Oversubscribed',
  ready: 'Ready to confirm',
  below_minimum: 'Below the minimum',
  placed: 'All placed',
};

/** One course a teacher runs this round. */
export type EdpTeacherCourse = {
  courseId: string;
  reference: string;
  title: string;
  status: EdpCourseStatus;
  scheduleKind: EdpScheduleKind;
  /** Empty on a special-date course. */
  sessions: { day: EdpDay; block: EdpBlock }[];
  /** Empty on a weekly course. */
  meetings: { date: string; startsAt: string; endsAt: string }[];
  confirmedCount: number;
};

/** One member of staff and everything they run this round. */
export type EdpTeacherCourses = {
  /** Null for a visiting teacher, who has no account, and for a course nobody is named on. */
  teacherId: string | null;
  /** The visiting teacher's name, or null when nobody is named at all. */
  teacherName: string | null;
  courses: EdpTeacherCourse[];
};

/** One line of the office's triage screen: does this course need a decision? */
export type EdpCourseDemand = {
  courseId: string;
  reference: string;
  title: string;
  /** Every meeting of the course, in school order. Empty on a special-date course. */
  sessions: { day: EdpDay; block: EdpBlock }[];
  scheduleKind: EdpScheduleKind;
  /** The dates a special-date course meets on, earliest first. Empty on a weekly one. */
  meetings: { date: string; startsAt: string; endsAt: string }[];
  gradeLevels: number[];
  oneOnOne: boolean;
  /** `maxStudents`, or null when the course has no fixed maximum. */
  capacity: number | null;
  minStudents: number;
  /** How many choices point at this course, at any rank. */
  demand: number;
  /** Choices nobody has answered yet — the office's actual to-do list, which `demand` is not. */
  pendingCount: number;
  confirmedCount: number;
  waitlistedCount: number;
  flag: EdpDemandFlag;
  /** The office's own decision. A cancelled course and one nobody joined both read "0 of 8" and
   *  carry `below_minimum`; only this says which of them is still waiting on somebody. */
  status: EdpCourseStatus;
};

/**
 * A row of the office's placement list: everyone who asked for the course, in the order the
 * rules put them, then everyone holding a seat on it who asked for nothing.
 */
export type EdpApplicant = {
  studentId: string;
  registrationId: string | null;
  /** The rank this course held on the family's list. 1 = first choice. Null on a seat-only row. */
  rank: number | null;
  gradeLevel: number | null;
  submittedAt: string | null;
  /**
   * This child holds a seat with no choice behind it — the office added them by hand, or the
   * family's later re-submission dropped the course from under a seat they already had. They
   * competed for nothing, so they carry no rank of either kind and are listed last.
   */
  seatOnly: boolean;
  studentName: string;
  gradeLabel: string;
  /** The seat this child already holds on this course, or null when they hold none. */
  seatStatus: EdpEnrolmentStatus | null;
  waitlistPosition: number | null;
  /** Informs, never blocks. §11 lets the school approve exceptions. */
  gradeEligible: boolean;
  /**
   * This course's rank against only the family's OTHER choices for the same afternoon(s) it
   * occupies — not `rank`, which is their whole-list position and would contradict the
   * parent-facing week (see `EdpWeek` in risp-frontend), which already ranks per slot. Null on a
   * special-date course, which holds no afternoon to rank within, and on a seat-only row, which
   * ranked nothing at all.
   */
  slotRank: number | null;
  /**
   * The family's other requests for THIS afternoon — the ones competing with this seat, which is
   * the same set `slotRank` counts. A request on another afternoon has no bearing on who is
   * placed here. Empty on a seat-only row, and on a special-date course, which holds no
   * afternoon for anything to compete within.
   */
  sameSlotChoices: EdpApplicantSameSlotChoice[];
};

/** One request competing with this one for the same afternoon, named as every EDP screen names a course. */
export type EdpApplicantSameSlotChoice = {
  courseId: string;
  courseTitle: string;
  rank: number;
  outcome: EdpChoiceOutcome;
  scheduleKind: EdpScheduleKind;
  sessions: { day: EdpDay; block: EdpBlock }[];
  meetings: { date: string; startsAt: string; endsAt: string }[];
};

export type EdpPlacementDecision = 'confirm' | 'offer' | 'waitlist' | 'withdraw';

/** What the office has done about a payment a family reported. A person decides; nothing talks to the bank. */
export type EdpPaymentStatus = 'declared' | 'confirmed' | 'rejected';

/** One child a payment covers, and their share of it on the day it was reported. */
/** One course a child holds a seat in, at its list price — before any free place is applied. */
export type EdpPaymentChildCourse = { title: string; price: number };

/** `amount` can be less than the courses' sum when a free place covers part of it. */
export type EdpPaymentChild = {
  studentId: string;
  studentName: string;
  amount: number;
  courses: EdpPaymentChildCourse[];
  /** Free places granted to the child for the round, how many the courses spent, and the baht off. */
  freePlaces: number;
  freePlacesUsed: number;
  discount: number;
};

/**
 * One family's report that they have paid for a round.
 *
 * Per household, not per child: the school is paid by one transfer covering every child a parent
 * has in the round, so `children` is what this one payment settles.
 */
/** How the money reached the school. A family's own report is always a transfer. */
export type EdpPaymentMethod = 'bank_transfer' | 'cash' | 'credit_card' | 'salary_deduction';

export const EDP_PAYMENT_METHODS: EdpPaymentMethod[] = ['bank_transfer', 'cash', 'credit_card', 'salary_deduction'];

export type EdpPayment = {
  id: string;
  amount: number;
  note: string | null;
  status: EdpPaymentStatus;
  method: EdpPaymentMethod;
  declaredAt: string;
  /** Whoever typed the record — the parent, or a member of the office for a desk payment. */
  declaredByName: string;
  /** The parents the money is for, both of them; null when none is on file for the children. */
  familyName: string | null;
  /** A member of staff entered this at the desk; the family did not report it. */
  recordedByOffice: boolean;
  hasDocument: boolean;
  documentName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  children: EdpPaymentChild[];
  /** The live receipt against this payment, if one has been issued. Null on every declared or
   *  rejected payment, and on a confirmed one nobody has receipted yet. */
  receiptId: string | null;
  receiptNumber: string | null;
  /** False both when there is no receipt and when there is one whose render failed — tells the
   *  office "not issued" apart from "issued, but the PDF still needs generating". */
  receiptHasPdf: boolean;
};

/** One issued receipt, as the office reads it back straight off `POST .../receipt`. */
export type EdpReceipt = {
  id: string;
  number: string;
};

/**
 * One household's money for a round. Every figure is derived — `owed` is the current seats priced
 * as they stand, so it moves on its own when the office changes anything, and a swap to a
 * same-price course never appears here at all.
 */
export type EdpHouseholdBalance = {
  householdKey: string;
  /** The household's parents — not whoever typed the record, which for a desk payment is a
   *  member of the office rather than anyone in the family. */
  familyName: string | null;
  /** At least one of this household's payments was entered by the office. */
  hasOfficeRecord: boolean;
  children: EdpPaymentChild[];
  owed: number;
  paid: number;
  /** Positive: still to come in. Negative: the school holds more than the family now owes. */
  balance: number;
  reportedAwaitingReview: number;
  paymentIds: string[];
};

/** Mirrors `EdpRoundPaymentTotals`: what one round adds up to, for the accounts. */
export type EdpRoundPaymentTotals = {
  children: number;
  fees: number;
  discount: number;
  payable: number;
  freePlaces: { granted: number; used: number; unused: number };
  confirmed: { total: number; byMethod: Record<EdpPaymentMethod, number> };
  awaitingReview: { count: number; amount: number };
  rejected: number;
  households: { total: number; settled: number; owing: number; overpaid: number };
  stillToCome: number;
  heldOver: number;
};

/**
 * One EDP course a member of staff teaches on one day — their own afternoon, beside the morning's
 * lessons on "Classes today". The counts are the same pair a lesson card carries.
 */
/** One course a member of staff runs this round, with its schedule and its children. */
export type EdpMyRunningCourse = {
  courseId: string;
  reference: string;
  courseTitle: string;
  gradeLevels: number[];
  sessions: {
    day: EdpDay;
    block: EdpBlock;
    blockLabel: string;
    startTime: string;
    endTime: string;
    roomName: string | null;
  }[];
  meetings: { date: string; startsAt: string; endsAt: string; roomName: string | null }[];
  total: number;
  students: { studentId: string; studentName: string; gradeLabel: string }[];
};

export type EdpMyCourse = {
  courseId: string;
  courseTitle: string;
  block: EdpBlock;
  /** The block's name, or "Special date" for a course that keeps its own dates. */
  blockLabel: string;
  /** `HH:MM`, or empty for a Saturday slot, which is one of the school's own morning periods. */
  startTime: string;
  endTime: string;
  roomName: string | null;
  total: number;
  markedCount: number;
};

export const EDP_PAYMENT_STATUS_LABELS: Record<EdpPaymentStatus, string> = {
  declared: 'Reported',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
};

/**
 * One course's full detail: everything `EdpCourse` already carries (including `price`, which
 * previously had nowhere to be set FROM — see the doc comment there) plus the same demand
 * figures and flag `EdpCourseDemand` carries for the whole round's triage screen. Fetched
 * directly by `CoursePlacement` rather than relying on router state passed from
 * `EdpRegistrations`, so a direct link or a page reload shows the same "oversubscribed" answer
 * a click-through would have.
 */
export type EdpCourseDetail = EdpCourse & {
  demand: number;
  pendingCount: number;
  confirmedCount: number;
  waitlistedCount: number;
  capacity: number | null;
  flag: EdpDemandFlag;
};

/** Why a family in the round was passed over — see `EdpSkipReason` on the API. */
export type EdpSkipReason = 'pending_choices' | 'no_guardian' | 'no_child_name';

/** A family nobody will write to unless the office does something about it. */
export type EdpSkippedFamily = { studentId: string; childName: string | null; reason: EdpSkipReason };

/**
 * What `POST /edp/rounds/:roundId/send-outcomes` reports back — see `EdpSendOutcomesResult` in
 * `edp-notifications.service.ts` on the API.
 *
 * `queued`, not `sent`: the API answers as soon as it knows who it is writing to, and the round
 * goes out behind that response. A later click reporting `queued: 0` is the office's own signal
 * that everyone has been written to.
 */
export type EdpSendOutcomesResult = { queued: number; skipped: EdpSkippedFamily[] };

// ── The afternoon list ("who leaves for EDP, who waits to be collected") ───

export type EdpAfternoonScope = 'homeroom' | 'class' | 'school';

/** One child staying for EDP this afternoon, and when they will be free. Mirrors
 *  `EdpAfternoonStayingResponse` on the API field for field. */
export type EdpAfternoonStaying = {
  studentId: string;
  studentName: string;
  gradeLevel: number | null;
  /** Which course, so the list can link straight to that room's register. */
  courseId: string;
  courseTitle: string;
  block: EdpBlock;
  /** '16:30' or '17:30' — when this child is free. */
  until: string;
  roomName: string | null;
  /**
   * The door's note that this child is not going to EDP today, and who took it. A one-day note:
   * the seat is still theirs, which is why the row stays on the roster (struck through) rather
   * than disappearing from it — a teacher expecting the child in the room needs to read why.
   */
  excusal: { reason: string; recordedByName: string } | null;
};

/** One child going straight home this afternoon. Mirrors `EdpAfternoonGoingHomeResponse`. */
export type EdpAfternoonGoingHome = {
  studentId: string;
  studentName: string;
  gradeLevel: number | null;
};

/**
 * Mirrors `EdpAfternoonResponse` on the API field for field. `goingHome` comes back empty
 * with `goingHomeOmitted: true` for the whole-school scope — at four hundred children that
 * list is noise, so the UI must read the flag rather than treat an empty array as "nobody
 * goes home".
 */
export type EdpAfternoonResponse = {
  date: string;
  weekday: number;
  /** '' when a teacher homerooms no class this year — not an error. */
  scopeLabel: string;
  staying: EdpAfternoonStaying[];
  goingHome: EdpAfternoonGoingHome[];
  goingHomeOmitted: boolean;
};

// ── The week overview (every register of a week side by side) ─────────────

/** One mark on the week grid. Mirrors `EdpAttendanceWeekMarkResponse`. */
export type EdpAttendanceWeekMark = { date: string; block: EdpBlock; status: AttendanceStatus };

/** One child on a course's roster, with whatever marks the week holds for them. A meeting with
 *  no entry in `marks` is unmarked. */
export type EdpAttendanceWeekStudent = {
  studentId: string;
  name: string;
  gradeLabel: string;
  marks: EdpAttendanceWeekMark[];
};

/** One meeting a course holds this week — a column of the grid. */
export type EdpAttendanceWeekMeeting = { date: string; block: EdpBlock; blockLabel: string; roomName: string | null };

/** One course meeting this week: meetings across the top, roster down the side. */
export type EdpAttendanceWeekCourse = {
  courseId: string;
  courseTitle: string;
  instructorName: string | null;
  /** Earliest first, never empty: a course with no meeting this week is not listed. */
  meetings: EdpAttendanceWeekMeeting[];
  students: EdpAttendanceWeekStudent[];
};

/** Mirrors `EdpAttendanceWeekResponse`: Monday to Saturday, Block 1 courses first. */
export type EdpAttendanceWeek = { weekStart: string; weekEnd: string; courses: EdpAttendanceWeekCourse[] };

/** One afternoon a child is in EDP during the week. Mirrors `EdpAfternoonWeekEntryResponse`. */
export type EdpAfternoonWeekEntry = {
  date: string;
  courseId: string;
  courseTitle: string;
  block: EdpBlock;
  blockLabel: string;
  until: string | null;
  roomName: string | null;
};

export type EdpAfternoonWeekStudent = {
  studentId: string;
  studentName: string;
  gradeLevel: number | null;
  /** Empty means home every afternoon of the week. */
  entries: EdpAfternoonWeekEntry[];
};

/** Mirrors `EdpAfternoonWeekResponse`: a class's week of EDP, child by child. */
export type EdpAfternoonWeek = {
  weekStart: string;
  weekEnd: string;
  /** '' for a teacher who homerooms no class this year — not an error. */
  scopeLabel: string;
  /** The days the school is open to students that week, YYYY-MM-DD. */
  days: string[];
  students: EdpAfternoonWeekStudent[];
};

export type EdpAfternoonWeekParams = {
  date: string;
  scope: 'homeroom' | 'class';
  classId?: string;
};

export type EdpAfternoonParams = {
  date: string;
  scope?: EdpAfternoonScope;
  /** Required, and only consulted, when `scope` is `'class'`. */
  classId?: string;
};

// ── The register ("who was in the room this afternoon") ─────────────────────

/** One child on a register, and the mark they carry for this meeting. Mirrors
 *  `EdpRegisterStudentResponse` on the API field for field. */
export type EdpRegisterStudent = {
  studentId: string;
  name: string;
  /** Null until someone marks the register. */
  status: AttendanceStatus | null;
  note: string | null;
  /** Who said so. Any member of staff may mark any register, so this is the accountability. */
  markedBy: string | null;
  /** ISO-8601, when the mark was last written. */
  markedAt: string | null;
  /**
   * Whether this child's family still owes for the round. Null for everyone but an administrator,
   * and always null for a teacher: an EDP instructor supervises the room, and whether a family is
   * behind on a bill is the office's to know and the office's to act on.
   */
  unpaid: boolean | null;
  /** True for a seat the API has already prefilled and refuses any other mark for — a
   *  suspension without EDP allowed. The register renders it read-only and never sends a
   *  mark for it back; the API would 409. */
  locked: boolean;
  lockReason: 'suspended' | null;
};

/**
 * The little a register screen needs about the course itself. Its own endpoint because
 * `GET /edp/courses/:courseId` is the office's — admin-only and `edp`-module gated — and would
 * 403 exactly the member of staff who is standing in the room at 16:25.
 */
/** What the school does about a meeting that did not happen. */
export type EdpCancellationSettlement = 'to_decide' | 'make_up' | 'refund';

export const EDP_CANCELLATION_SETTLEMENTS: EdpCancellationSettlement[] = ['to_decide', 'make_up', 'refund'];

/** One afternoon of a course the office struck out. Mirrors `EdpCancellationResponse`. */
export type EdpCancellation = {
  id: string;
  courseId: string;
  courseTitle: string;
  courseReference: string;
  date: string;
  block: EdpBlock;
  blockLabel: string;
  reason: string;
  settlement: EdpCancellationSettlement;
  makeUpDate: string | null;
  makeUpBlock: EdpBlock | null;
  makeUpBlockLabel: string | null;
  note: string | null;
  createdAt: string | null;
};

/**
 * Where one lesson's register stands. `taken` / `partial` / `not_taken` are said only of a lesson
 * that has ended (or is fully marked); `moved` is one marked on a day the course no longer meets
 * on — readable from the history, not markable.
 */
export type EdpLessonState =
  'upcoming' | 'today' | 'taken' | 'partial' | 'not_taken' | 'no_children' | 'cancelled' | 'moved';

/** One lesson of a course. Mirrors `EdpRegisterLessonResponse`. */
export type EdpRegisterLesson = {
  date: string;
  block: EdpBlock;
  label: string;
  state: EdpLessonState;
  roster: number;
  marked: number;
  attended: number;
  markedBy: string | null;
  markedAt: string | null;
  makeUpFor: string | null;
  cancellation: EdpCancellation | null;
};

/** One lesson in the round-wide list of registers. Mirrors `EdpRoundRegisterResponse`. */
export type EdpRoundRegister = EdpRegisterLesson & {
  courseId: string;
  courseTitle: string;
  courseReference: string;
  instructorName: string | null;
  assistantNames: string[];
};

export type CancelEdpMeetingData = { date: string; block: EdpBlock; reason: string };

export type SettleEdpCancellationData = {
  settlement: EdpCancellationSettlement;
  makeUpDate?: string | null;
  makeUpBlock?: EdpBlock | null;
  note?: string | null;
  reason?: string;
};

/** An afternoon the office struck out, as the register hears of it. */
export type EdpRegisterCancellation = {
  date: string;
  block: EdpBlock;
  reason: string;
  settlement: EdpCancellationSettlement;
  makeUpDate: string | null;
};

export type EdpRegisterCourse = {
  courseId: string;
  courseTitle: string;
  /** Empty on a special-date course. */
  sessions: { day: EdpDay; block: EdpBlock }[];
  scheduleKind: EdpScheduleKind;
  /** The dates a special-date course meets on, earliest first. Empty on a weekly one — and the
   *  only thing the register's date picker can offer, since there is no weekday to walk. */
  meetings: { date: string; startsAt: string; endsAt: string }[];
  /** Afternoons struck out by the office: the register says so rather than offering a refusal. */
  cancellations: EdpRegisterCancellation[];
  /** The round's first and last lesson dates. No register exists outside them. */
  firstLessonDate: string;
  lastLessonDate: string;
};

export type EdpRegister = {
  courseId: string;
  courseTitle: string;
  date: string;
  block: EdpBlock;
  students: EdpRegisterStudent[];
};

/** What staff say about one child at one meeting. A child left unmarked is simply left out. */
export type EdpRegisterMark = { studentId: string; status: AttendanceStatus; note?: string | null };

export type EdpRegisterMarksData = { date: string; block: EdpBlock; marks: EdpRegisterMark[] };

/**
 * One mark from a course's whole history, whichever days the course meets on now.
 *
 * The register itself refuses a date the course no longer meets on — right for marking, and the
 * reason this exists: a term marked on Tuesdays has to stay readable after the office moves the
 * course to Wednesday.
 */
export type EdpRegisterHistoryEntry = {
  date: string;
  block: EdpBlock;
  studentId: string;
  name: string;
  status: AttendanceStatus;
  note: string | null;
  markedBy: string | null;
  markedAt: string | null;
};

/** One child's whole term in a course. The five counts always add up to `meetings`. */
export type EdpAttendanceSummaryStudent = {
  studentId: string;
  name: string;
  present: number;
  absent: number;
  late: number;
  excused: number;
  /** Afternoons inside a suspension without EDP — shown only when some child has one. */
  suspended: number;
  /** Afternoons nobody has said either way about — not absences. */
  unmarked: number;
};

/**
 * What a course's register adds up to so far.
 *
 * `attendanceRate` is null, never 0, until something has been marked: an untouched register means
 * nobody has taken it, not that nobody came. `unmarkedMeetings` travels with the rate for the same
 * reason — registers are filled in days late, and a rate is only as complete as the register under
 * it, so no screen may show one without the other.
 */
export type EdpAttendanceSummary = {
  /** Meetings held so far. */
  meetings: number;
  /** Meetings the whole round holds, the ones still to come included. */
  termMeetings: number;
  /** Meetings carrying at least one mark — not the same as a register anyone has finished. */
  markedMeetings: number;
  /** Meetings where every child on the roster has a mark. The only honest basis for "all done". */
  fullyMarkedMeetings: number;
  unmarkedMeetings: number;
  /**
   * Marked meetings the course no longer meets on: the office has moved it to another day. While
   * this is non-zero the meeting counts are an estimate — they are derived from the days the
   * course meets on now, so they also count days it never met on — and a screen must say so.
   */
  offScheduleMeetings: number;
  /** Attended over marked, 0-1. Present and late count as attended; excused does not. Null while
   *  no child on the roster has been marked. */
  attendanceRate: number | null;
  students: EdpAttendanceSummaryStudent[];
};

/**
 * The master schedule's whole response, aliased straight off the OpenAPI document the API emits
 * rather than hand-copied here.
 *
 * Hand-copying is what let the route rename `slots` to `courses` while this file still compiled
 * and the screen quietly drew an empty week. Aliased, a renamed or retyped field in
 * `EdpScheduleResponse` becomes a compile error in the page that reads it, the next time
 * `yarn generate-openapi` runs.
 */
type ScheduleSchemas = components['schemas'];

/** One grade band as the master schedule groups it. Sent by the API so no screen restates the list. */
export type EdpScheduleBand = ScheduleSchemas['EdpScheduleBandResponse'];

/** One weekly meeting of a course: which afternoon it keeps, and where. */
export type EdpScheduleSession = ScheduleSchemas['EdpScheduleSessionResponse'];

/**
 * One course in the round, sent exactly once however many bands or afternoons it straddles. Its
 * `gradeLevels` are what the strip spans a bar across, so grade stays a continuous axis rather
 * than being diced into bands the way the old cell grid did.
 */
export type EdpScheduleGridCourse = ScheduleSchemas['EdpScheduleGridCourseResponse'];

/** A band with no course at all on a weekday. Saturday is never counted — the template is Mon–Fri. */
export type EdpScheduleGap = ScheduleSchemas['EdpScheduleGapResponse'];

export type EdpScheduleBalance = ScheduleSchemas['EdpScheduleBalanceResponse'];

export type EdpScheduleOverMax = ScheduleSchemas['EdpScheduleOverMaxResponse'];

/** One enumerated meeting of a `special_dates` course. */
export type EdpScheduleMeeting = ScheduleSchemas['EdpScheduleMeetingResponse'];

/**
 * A `special_dates` course, as the master schedule lists it: it holds no day or block, so it never
 * appears in `courses` and never closes a `gaps` entry — see `EdpSchedule`.
 */
export type EdpScheduleSpecialDate = ScheduleSchemas['EdpScheduleSpecialDateResponse'];

/**
 * The office's whole week for one round. `courses` is the week itself — which afternoons each
 * course keeps and which grades it serves — while `bands` names only the checks beneath it.
 */
export type EdpSchedule = ScheduleSchemas['EdpScheduleResponse'];

/** One course a child holds a seat on, priced. `billed` is the API's own answer to whether
 *  `computeEdpCost` counted it — never re-derived from the status here. */
export type EdpStudentSeat = ScheduleSchemas['EdpStudentSeatResponse'];

/** One child in a round: what they are doing, and what their family owes for it. */
export type EdpStudentRow = ScheduleSchemas['EdpStudentRowResponse'];

/**
 * The round read per child rather than per course. Every figure on it is computed by the API's
 * `computeEdpCost`, which is also what the parent's estimate and the family's bill run on — the
 * admin displays them and never recomputes them.
 */
export type EdpStudents = ScheduleSchemas['EdpStudentsResponse'];

/** A discount the office granted against one child's bill for a round. */
export type EdpAdjustment = {
  id: string;
  roundId: string;
  studentId: string;
  amount: number;
  reason: string;
  createdById: string;
  createdAt: string;
  createdBy?: { id: string; fullName: string | null; nickname: string | null } | null;
};

function query(filters: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const edpApi = {
  openRound: () => fetchApi<EdpRound | null>(`${EDP_PATH}/rounds/open`),
  listRounds: () => fetchApi<EdpRound[]>(`${EDP_PATH}/rounds`),
  createRound: (data: CreateEdpRoundData) =>
    fetchApi<EdpRound>(`${EDP_PATH}/rounds`, { method: 'POST', body: JSON.stringify(data) }),
  updateRound: (id: string, data: UpdateEdpRoundData) =>
    fetchApi<EdpRound>(`${EDP_PATH}/rounds/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  submit: (data: SubmitEdpProposalsData) =>
    fetchApi<EdpProposalSummary[]>(`${EDP_PATH}/proposals`, { method: 'POST', body: JSON.stringify(data) }),
  listMine: () => fetchApi<EdpProposalSummary[]>(`${EDP_PATH}/proposals/me`),
  queue: (filters: EdpProposalFilters) =>
    fetchApi<EdpProposalSummary[]>(`${EDP_PATH}/proposals${query(filters as Record<string, string | undefined>)}`),
  get: (id: string) => fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}`),
  resubmit: (id: string, data: EdpCoursePayload) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** The office correcting a proposal in place, rather than sending it back for a typo. */
  editByOffice: (id: string, data: EdpCoursePayload) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/office-edit`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** Another group of an already-approved proposal, when demand outruns the places opened. */
  addGroup: (id: string, data: EdpCourseGroupData) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/groups`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  withdraw: (id: string) => fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/withdraw`, { method: 'POST' }),
  comment: (id: string, body: string) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/comment`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  approve: (id: string, data: ApproveEdpProposalData) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  requestChanges: (id: string, data: EdpDecisionData) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/request-changes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  waitlist: (id: string, data: EdpDecisionData) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/waitlist`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  reject: (id: string, data: EdpDecisionData) =>
    fetchApi<EdpProposalDetail>(`${EDP_PATH}/proposals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  configureCourse: (courseId: string, data: ConfigureEdpCourseData) =>
    fetchApi<void>(`${EDP_PATH}/courses/${courseId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // ── Office registration & placement (subsystem 3) ─────────────────────────
  getCourse: (courseId: string) => fetchApi<EdpCourseDetail>(`${EDP_PATH}/courses/${courseId}`),
  demand: (roundId: string) => fetchApi<EdpCourseDemand[]>(`${EDP_PATH}/rounds/${roundId}/demand`),
  coursesByTeacher: (roundId: string) =>
    fetchApi<EdpTeacherCourses[]>(`${EDP_PATH}/rounds/${roundId}/courses-by-teacher`),
  schedule: (roundId: string) => fetchApi<EdpSchedule>(`${EDP_PATH}/rounds/${roundId}/schedule`),
  students: (roundId: string) => fetchApi<EdpStudents>(`${EDP_PATH}/rounds/${roundId}/students`),
  adjustments: (roundId: string, studentId: string) =>
    fetchApi<EdpAdjustment[]>(`${EDP_PATH}/rounds/${roundId}/students/${studentId}/adjustments`),
  createAdjustment: (roundId: string, studentId: string, data: { amount: number; reason: string }) =>
    fetchApi<EdpAdjustment>(`${EDP_PATH}/rounds/${roundId}/students/${studentId}/adjustments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removeAdjustment: (id: string) => fetchApi<void>(`${EDP_PATH}/adjustments/${id}`, { method: 'DELETE' }),
  applicants: (courseId: string) => fetchApi<EdpApplicant[]>(`${EDP_PATH}/courses/${courseId}/applicants`),
  payments: (roundId: string) => fetchApi<EdpPayment[]>(`${EDP_PATH}/rounds/${roundId}/payments`),
  paymentBalances: (roundId: string) =>
    fetchApi<EdpHouseholdBalance[]>(`${EDP_PATH}/rounds/${roundId}/payment-balances`),
  paymentTotals: (roundId: string) => fetchApi<EdpRoundPaymentTotals>(`${EDP_PATH}/rounds/${roundId}/payment-totals`),
  /**
   * A payment the office takes at the desk. Multipart, because the counter receipt travels with
   * the amount — `fetchApi` leaves the Content-Type to the browser when handed a FormData body,
   * which is what lets it set the multipart boundary.
   */
  recordPayment: async (
    roundId: string,
    data: { studentId: string; amount: number; note?: string; method?: EdpPaymentMethod; slip?: File | null }
  ) => {
    const form = new FormData();
    form.append('studentId', data.studentId);
    form.append('amount', String(data.amount));
    if (data.note) form.append('note', data.note);
    if (data.method) form.append('method', data.method);
    // Shrunk and read into memory first: a raw phone photo on the school's uplink outlives the
    // request, and the upload dies half way. See `prepareFileForUpload`.
    if (data.slip) form.append('slip', await prepareFileForUpload(data.slip));
    return fetchApi<EdpPayment>(`${EDP_PATH}/rounds/${roundId}/payments`, {
      method: 'POST',
      body: form,
      timeoutMs: UPLOAD_TIMEOUT_MS,
    });
  },
  reviewPayment: (paymentId: string, data: { confirm: boolean; note?: string }) =>
    fetchApi<EdpPayment>(`${EDP_PATH}/payments/${paymentId}/review`, { method: 'POST', body: JSON.stringify(data) }),
  sendPaymentNotices: (roundId: string) =>
    fetchApi<EdpSendOutcomesResult>(`${EDP_PATH}/rounds/${roundId}/payment-notices`, { method: 'POST' }),
  /** The chase. Unlike the notice beside it, this may be sent as often as the office likes. */
  sendPaymentReminders: (roundId: string) =>
    fetchApi<EdpSendOutcomesResult>(`${EDP_PATH}/rounds/${roundId}/payment-reminders`, { method: 'POST' }),
  /**
   * Opened in a new tab rather than fetched: the slip is a file the office looks at, not data.
   * Absolute, because the browser resolves it itself — a bare path would go to the admin app's own
   * origin, which has no such route, and the link would appear to do nothing. Same shape as
   * `allergy-change-requests.documentUrl` and `infirmary-visits.attachmentDownloadUrl`.
   */
  updatePayment: (paymentId: string, data: { amount?: number; note?: string; method?: EdpPaymentMethod }) =>
    fetchApi<EdpPayment>(`${EDP_PATH}/payments/${paymentId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePayment: (paymentId: string) => fetchApi<void>(`${EDP_PATH}/payments/${paymentId}`, { method: 'DELETE' }),
  paymentSlipUrl: (paymentId: string) => `${API_BASE_URL}${EDP_PATH}/payments/${paymentId}/slip`,
  createReceipt: (paymentId: string) =>
    fetchApi<EdpReceipt>(`${EDP_PATH}/payments/${paymentId}/receipt`, { method: 'POST' }),
  createAllReceipts: (roundId: string) =>
    fetchApi<{ issued: number; skipped: number }>(`${EDP_PATH}/rounds/${roundId}/receipts`, { method: 'POST' }),
  /**
   * Opened in a new tab, same as `paymentSlipUrl` beside it — absolute, because a bare path
   * resolves against the admin app's own origin, which has no such route, and the link would
   * appear to do nothing.
   */
  receiptPdfUrl: (receiptId: string) => `${API_BASE_URL}${EDP_PATH}/receipts/${receiptId}/pdf`,
  /** Withdraws a wrongly-issued receipt, freeing its payment to be edited or deleted again. */
  voidReceipt: (receiptId: string, reason: string) =>
    fetchApi<EdpReceipt>(`${EDP_PATH}/receipts/${receiptId}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  /** Re-renders a receipt's PDF after an earlier render failed. Never allocates a number. */
  regenerateReceipt: (receiptId: string) =>
    fetchApi<{ documentFilename: string }>(`${EDP_PATH}/receipts/${receiptId}/regenerate`, { method: 'POST' }),
  decide: (courseId: string, studentId: string, decision: EdpPlacementDecision) =>
    fetchApi<void>(`${EDP_PATH}/courses/${courseId}/applicants/${studentId}`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),
  /** Takes a child off the course entirely — the seat and the request behind it. Irreversible. */
  removeApplicant: (courseId: string, studentId: string) =>
    fetchApi<void>(`${EDP_PATH}/courses/${courseId}/applicants/${studentId}`, { method: 'DELETE' }),
  confirmAll: (courseId: string) =>
    fetchApi<{ confirmed: number; skipped: number }>(`${EDP_PATH}/courses/${courseId}/confirm-all`, {
      method: 'POST',
    }),
  enrolManually: (courseId: string, studentId: string) =>
    fetchApi<void>(`${EDP_PATH}/courses/${courseId}/enrolments`, {
      method: 'POST',
      body: JSON.stringify({ studentId }),
    }),
  sendOutcomes: (roundId: string) =>
    fetchApi<EdpSendOutcomesResult>(`${EDP_PATH}/rounds/${roundId}/send-outcomes`, { method: 'POST' }),

  registerCourse: (courseId: string) => fetchApi<EdpRegisterCourse>(`${EDP_PATH}/courses/${courseId}/register/course`),
  /** The EDP courses the signed-in member of staff teaches on a date. */
  myRunning: () => fetchApi<EdpMyRunningCourse[]>(`${EDP_PATH}/my/running`),
  myCourses: (date: string) => fetchApi<EdpMyCourse[]>(`${EDP_PATH}/my/courses?date=${encodeURIComponent(date)}`),
  register: (courseId: string, date: string, block: EdpBlock) =>
    fetchApi<EdpRegister>(`${EDP_PATH}/courses/${courseId}/register${query({ date, block })}`),
  markRegister: (courseId: string, data: EdpRegisterMarksData) =>
    fetchApi<void>(`${EDP_PATH}/courses/${courseId}/register`, { method: 'PUT', body: JSON.stringify(data) }),
  registerHistory: (courseId: string) =>
    fetchApi<EdpRegisterHistoryEntry[]>(`${EDP_PATH}/courses/${courseId}/register/history`),
  registerSummary: (courseId: string) =>
    fetchApi<EdpAttendanceSummary>(`${EDP_PATH}/courses/${courseId}/register/summary`),
  registerLessons: (courseId: string) =>
    fetchApi<EdpRegisterLesson[]>(`${EDP_PATH}/courses/${courseId}/register/lessons`),
  roundRegisters: (roundId: string, range: { from?: string; to?: string }) =>
    fetchApi<EdpRoundRegister[]>(`${EDP_PATH}/rounds/${roundId}/registers${query(range)}`),

  afternoon: (params: EdpAfternoonParams) =>
    fetchApi<EdpAfternoonResponse>(
      `${EDP_PATH}/afternoon${query({ date: params.date, scope: params.scope, classId: params.classId })}`
    ),
  afternoonWeek: (params: EdpAfternoonWeekParams) =>
    fetchApi<EdpAfternoonWeek>(
      `${EDP_PATH}/afternoon/week${query({ date: params.date, scope: params.scope, classId: params.classId })}`
    ),
  cancellations: (roundId: string) => fetchApi<EdpCancellation[]>(`${EDP_PATH}/rounds/${roundId}/cancellations`),
  cancelMeeting: (courseId: string, data: CancelEdpMeetingData) =>
    fetchApi<EdpCancellation>(`${EDP_PATH}/courses/${courseId}/cancellations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  settleCancellation: (id: string, data: SettleEdpCancellationData) =>
    fetchApi<EdpCancellation>(`${EDP_PATH}/cancellations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeCancellation: (id: string) => fetchApi<void>(`${EDP_PATH}/cancellations/${id}`, { method: 'DELETE' }),
  /** Every course meeting in the week around `date`, with each register's marks. */
  attendanceWeek: (date: string) => fetchApi<EdpAttendanceWeek>(`${EDP_PATH}/attendance/week${query({ date })}`),
};

export const fetchEdpAfternoon = edpApi.afternoon;

export const fetchEdpDemand = edpApi.demand;
export const fetchEdpApplicants = edpApi.applicants;
export const decideEdpApplicant = edpApi.decide;
export const confirmAllEdpApplicants = edpApi.confirmAll;
export const enrolEdpStudent = edpApi.enrolManually;
